import { useMemo, useState } from "react";
import type { CSSProperties, HTMLAttributes, ReactNode } from "react";
import type { TimeDay } from "../api/types";
import { buildWeekBoard } from "../lib/weekBoard";
import type {
  BoardBlock,
  BoardDay,
  BoardDayActivity,
  BoardOverlap,
} from "../lib/weekBoard";

interface Props {
  periodStart: string;
  periodEnd: string;
  /** Jours calculés par le serveur ; un jour absent est rendu vide. */
  daysByKey: Map<string, TimeDay>;
  /** Libellés du catalogue, par code : le code seul ne parle pas à un manager. */
  labels?: Map<string, string>;
}

export const DOW = [
  "dimanche",
  "lundi",
  "mardi",
  "mercredi",
  "jeudi",
  "vendredi",
  "samedi",
];
const MONTHS = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
];

/** Midi UTC pour lire le jour de la semaine sans qu'un fuseau ne recule d'un jour. */
const at = (dayKey: string) => new Date(`${dayKey}T12:00:00Z`);
export const hm = (m: number) =>
  `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(Math.round(m % 60)).padStart(2, "0")}`;
export const dur = (m: number) =>
  `${Math.floor(m / 60)}h${String(Math.round(m % 60)).padStart(2, "0")}`;

/** Largeur d'une bande de couleur dans une zone de chevauchement, et du liseré
 * qui la sépare de la suivante (px, perpendiculairement aux rayures). */
const OVERLAP_BAND_PX = 7;
const OVERLAP_RULE_PX = 1.5;

interface Tip {
  x: number;
  y: number;
  content: ReactNode;
}

export type Follow = (content: ReactNode) => {
  onMouseMove: (e: { clientX: number; clientY: number }) => void;
  onMouseLeave: () => void;
};

/** Le tooltip suit le curseur, décalé pour ne pas passer sous lui, et se
 * recadre à droite pour ne pas sortir de la fenêtre : comme celui du kit. */
export function useFollowTip(): { tip: Tip | null; follow: Follow } {
  const [tip, setTip] = useState<Tip | null>(null);
  const follow: Follow = (content) => ({
    onMouseMove: (e) =>
      setTip({
        x: Math.min(e.clientX + 14, window.innerWidth - 280),
        y: e.clientY + 14,
        content,
      }),
    onMouseLeave: () => setTip(null),
  });
  return { tip, follow };
}

/** Dans .wb-root : le tooltip reprend --tooltip-bg/-ink du thème de la board.
 * En position fixed, l'imbrication ne change rien à son placement : aucun
 * ancêtre ne porte de transform. */
export function FriseTooltip({ tip }: { tip: Tip | null }) {
  return tip ? (
    <div className="wb-tooltip" style={{ left: tip.x, top: tip.y }}>
      {tip.content}
    </div>
  ) : null;
}

export interface FriseAxis {
  pct: (min: number) => number;
  width: (a: number, b: number) => number;
  gridlines: ReactNode;
}

/** Position en pourcentage sur l'axe de la board, et ses graduations. */
export function friseAxis(board: {
  startMin: number;
  endMin: number;
  ticks: number[];
}): FriseAxis {
  const span = board.endMin - board.startMin;
  const pct = (min: number) =>
    ((Math.min(Math.max(min, board.startMin), board.endMin) - board.startMin) /
      span) *
    100;
  const width = (a: number, b: number) => Math.max(pct(b) - pct(a), 0.18);
  const gridlines = board.ticks.map((t) => (
    <div key={t} className="wb-gridline" style={{ left: `${pct(t)}%` }} />
  ));
  return { pct, width, gridlines };
}

/** Graduations horaires de la board, en tête de piste. */
export function FriseAxisRow({
  ticks,
  pct,
}: {
  ticks: number[];
  pct: (min: number) => number;
}) {
  return (
    <div className="wb-axis">
      <div />
      <div className="wb-track">
        {ticks.map((t) => (
          <span key={t} className="wb-tick" style={{ left: `${pct(t)}%` }}>
            {String(Math.floor(t / 60)).padStart(2, "0")}h
          </span>
        ))}
      </div>
    </div>
  );
}

/** Une ligne par projet sous la ligne du jour : nom, durée, piste. */
export function FriseSubrow({
  name,
  color,
  meta,
  gridlines,
  className,
  children,
  ...rest
}: {
  name: ReactNode;
  color?: string;
  meta: ReactNode;
  gridlines: ReactNode;
  className?: string;
  children: ReactNode;
} & Omit<HTMLAttributes<HTMLDivElement>, "children">) {
  return (
    <div className={`wb-subrow${className ? ` ${className}` : ""}`} {...rest}>
      <div className="wb-plabel">
        <span className="wb-pname">
          <span
            className="wb-pdot"
            style={color ? { background: color } : {}}
          />
          {name}
        </span>
        <span className="wb-pdur">{meta}</span>
      </div>
      <div className="wb-ptrack">
        {gridlines}
        {children}
      </div>
    </div>
  );
}

/**
 * La semaine sur un axe horaire commun, une ligne par jour.
 *
 * Portage du rendu HTML de timesheet-kit, sur les données de TAWA : donc toutes
 * sources confondues, là où le kit ne voyait que lui-même. Deux éléments de son
 * visuel manquent et manqueront : la lane « perso » ne quitte jamais la machine
 * (CONSENT.md en fait une garantie) et la pastille d'activité nocturne suppose
 * une amplitude que buildBlocks borne déjà à 6h-22h.
 *
 * Complément de la vue jour, pas son remplacement : celle-ci ne s'édite pas.
 */
export function TimelineWeekBoard({
  periodStart,
  periodEnd,
  daysByKey,
  labels,
}: Props) {
  const board = useMemo(
    () => buildWeekBoard(periodStart, periodEnd, daysByKey),
    [periodStart, periodEnd, daysByKey],
  );
  const [openDay, setOpenDay] = useState<string | null>(null);
  const { tip, follow } = useFollowTip();
  const { pct, width, gridlines } = friseAxis(board);
  const nameOf = (code: string) =>
    code ? (labels?.get(code) ?? code) : "Présence hors activité";
  const colorOf = (code: string) =>
    board.totals.find((t) => t.code === code)?.color ?? "var(--wb-divers)";

  const totalMin = board.totals.reduce((s, t) => s + t.minutes, 0);

  /**
   * Rayures aux couleurs des activités en présence, dans leur ordre de
   * démarrage : la zone dit qui se chevauche, pas seulement qu'il y a conflit.
   *
   * Le liseré de surface entre deux bandes est ce qui fait lire des rayures
   * plutôt qu'un aplat : la palette contient deux verts voisins (--wb-s2 et
   * --wb-s4), et sans séparation ces deux-là se fondraient l'un dans l'autre
   * précisément quand il faut les distinguer.
   */
  const overlapFill = (o: BoardOverlap) => {
    const stops: string[] = [];
    let at = 0;
    for (const code of o.codes) {
      stops.push(`${colorOf(code)} ${at}px ${at + OVERLAP_BAND_PX}px`);
      at += OVERLAP_BAND_PX;
      stops.push(`var(--surface) ${at}px ${at + OVERLAP_RULE_PX}px`);
      at += OVERLAP_RULE_PX;
    }
    return `repeating-linear-gradient(45deg, ${stops.join(", ")})`;
  };

  /** « A + B », ou le compte au-delà de deux : trois libellés de projet sur une
   * ligne de tooltip débordent avant d'informer. */
  const overlapWho = (o: BoardOverlap) =>
    o.codes.length === 2
      ? `${nameOf(o.codes[0]!)} + ${nameOf(o.codes[1]!)}`
      : `${o.codes.length} activités en parallèle`;

  if (board.days.length === 0) {
    return (
      <div className="empty-state">
        {/* Composant vide de Chronos, hors .wb-root : ses classes sont celles de
            l'application, pas celles de la board. */}
        <div className="sub">Période invalide.</div>
      </div>
    );
  }

  const firstDay = at(board.days[0]!.dayKey);
  const lastDay = at(board.days[board.days.length - 1]!.dayKey);
  // Pas de « Timeline · » ni de titre de page : l'écran en porte déjà un, et
  // deux titres empilés se lisent comme une erreur de gabarit.
  // Le mot vient du nombre de jours rendus, pas d'un « weekly » supposé : une
  // board mensuelle (settings.timesheet_period_type) rend jusqu'à 31 lignes.
  const periodWord = board.days.length > 7 ? "Période" : "Semaine";
  const sameMonth =
    firstDay.getUTCMonth() === lastDay.getUTCMonth() &&
    firstDay.getUTCFullYear() === lastDay.getUTCFullYear();
  const title =
    `${periodWord} du ${String(firstDay.getUTCDate()).padStart(2, "0")}` +
    (sameMonth ? "" : ` ${MONTHS[firstDay.getUTCMonth()]}`) +
    ` au ${String(lastDay.getUTCDate()).padStart(2, "0")} ` +
    `${MONTHS[lastDay.getUTCMonth()]} ${lastDay.getUTCFullYear()}`;
  const daysWithWork = board.days.filter((d) => !d.empty).length;

  const segStyle = (b: BoardBlock, code: string): CSSProperties => ({
    left: `${pct(b.startMin)}%`,
    width: `${width(b.startMin, b.endMin)}%`,
    background: colorOf(code),
  });

  const rows: ReactNode[] = [];
  for (const day of board.days) {
    const d = at(day.dayKey);
    const dow = DOW[d.getUTCDay()] ?? "";
    const dd = String(d.getUTCDate()).padStart(2, "0");
    const open = openDay === day.dayKey;
    const toggle = () => setOpenDay(open ? null : day.dayKey);

    rows.push(
      <div
        key={day.dayKey}
        className={`wb-row wb-expandable${day.weekend ? " wb-weekend" : ""}`}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <div className="wb-daylabel">
          <div className="wb-name">
            <span className="wb-chev">▶</span>
            {dow} {dd}
          </div>
          <div className="wb-range">
            {day.empty ? (
              "-"
            ) : (
              <>
                {hm(day.firstMin)} → {hm(day.lastMin)} ·{" "}
                <span className="wb-total">{dur(day.workedMin)}</span>
              </>
            )}
          </div>
          {day.weekend && <div className="wb-we">hors déclaration</div>}
        </div>
        <div className="wb-track">
          {gridlines}
          <div className="wb-lane-work">
            {day.gaps.map((g) =>
              width(g.startMin, g.endMin) > 6 ? (
                <span
                  key={`gap-${g.startMin}`}
                  className="wb-pause-label"
                  style={{ left: `${(pct(g.startMin) + pct(g.endMin)) / 2}%` }}
                >
                  {dur(g.minutes)}
                </span>
              ) : null,
            )}
            {day.blocks.map((b, i) => (
              <button
                key={`${day.dayKey}-${i}`}
                type="button"
                className={`wb-seg${b.manual ? " wb-manual" : ""}`}
                style={segStyle(b, b.code)}
                {...follow(
                  <>
                    <b>{nameOf(b.code)}</b>
                    <br />
                    {hm(b.startMin)} → {hm(b.endMin)} ·{" "}
                    {dur(b.endMin - b.startMin)}
                    {b.manual && (
                      <>
                        <br />
                        Saisi à la main
                      </>
                    )}
                  </>,
                )}
                onClick={(e) => e.stopPropagation()}
                aria-label={`${nameOf(b.code)} ${hm(b.startMin)} à ${hm(b.endMin)}`}
              />
            ))}
            {/* Après les tranches, donc au-dessus d'elles : la zone rayée
                couvre exactement ce que la tranche peinte en dernier masque. */}
            {day.overlaps.map((o) => (
              <button
                key={`ov-${o.startMin}`}
                type="button"
                className="wb-overlap"
                style={
                  {
                    left: `${pct(o.startMin)}%`,
                    width: `${width(o.startMin, o.endMin)}%`,
                    // Variable plutôt que background-image : la feuille garde
                    // la main sur le motif, le composant ne fournit que les
                    // teintes, qu'elle seule ne peut pas connaître.
                    "--wb-overlap-fill": overlapFill(o),
                  } as CSSProperties
                }
                {...follow(
                  <>
                    <b>Chevauchement</b>
                    <br />
                    {hm(o.startMin)} → {hm(o.endMin)} · {dur(o.minutes)}
                    <br />
                    {overlapWho(o)}
                  </>,
                )}
                onClick={(e) => e.stopPropagation()}
                aria-label={`Chevauchement de ${hm(o.startMin)} à ${hm(o.endMin)}, ${overlapWho(o)}`}
              />
            ))}
          </div>
        </div>
      </div>,
    );

    if (open) {
      rows.push(
        <div key={`${day.dayKey}-sub`} className="wb-subrows">
          {day.activities.map((a: BoardDayActivity) => (
            <FriseSubrow
              key={a.code}
              name={nameOf(a.code)}
              color={colorOf(a.code)}
              meta={
                <>
                  plage {dur(a.lastMin - a.firstMin)} · décl. {dur(a.minutes)}
                </>
              }
              gridlines={gridlines}
            >
              <div
                className="wb-range-band"
                style={{
                  left: `${pct(a.firstMin)}%`,
                  width: `${width(a.firstMin, a.lastMin)}%`,
                  background: colorOf(a.code),
                }}
                {...follow(
                  <>
                    <b>{nameOf(a.code)}</b> : plage
                    <br />
                    {hm(a.firstMin)} → {hm(a.lastMin)} ·{" "}
                    {dur(a.lastMin - a.firstMin)}
                  </>,
                )}
              />
              {a.blocks.map((b, i) => (
                <div
                  key={`${a.code}-${i}`}
                  className="wb-seg wb-core"
                  style={segStyle(b, a.code)}
                />
              ))}
            </FriseSubrow>
          ))}
        </div>,
      );
    }
  }

  const detail = board.days.flatMap((day: BoardDay) => {
    const d = at(day.dayKey);
    return day.blocks
      .filter((b) => b.endMin - b.startMin >= 5)
      .map((b, i) => (
        <tr key={`${day.dayKey}-${i}`}>
          <td>
            {DOW[d.getUTCDay()]} {d.getUTCDate()}
          </td>
          <td>{hm(b.startMin)}</td>
          <td>{hm(b.endMin)}</td>
          <td>{nameOf(b.code)}</td>
          <td>{dur(b.endMin - b.startMin)}</td>
        </tr>
      ));
  });

  return (
    <>
      <div className="wb-root">
        <div className="wb-head">
          <span>{title}</span>
          <span className="wb-sep">·</span>
          <span>
            déclarable <b>{dur(totalMin)}</b>
          </span>
          <span className="wb-sep">·</span>
          <span>
            {daysWithWork} jour{daysWithWork > 1 ? "s" : ""} avec activité
          </span>
        </div>

        <div className="wb-chips">
          <div className="wb-chip wb-total">
            <span>Total déclarable</span>
            <span className="wb-dur">{dur(totalMin)}</span>
          </div>
          {board.totals.map((t) => (
            <div key={t.code} className="wb-chip">
              <span className="wb-dot" style={{ background: t.color }} />
              <span>{nameOf(t.code)}</span>
              <span className="wb-dur">{dur(t.minutes)}</span>
            </div>
          ))}
        </div>

        <div className="wb-board">
          <div className="wb-board-inner">
            <FriseAxisRow ticks={board.ticks} pct={pct} />
            <div>{rows}</div>
            <div className="wb-legend-note">
              <span className="wb-k">
                <span className="wb-swatch-gap" /> vide = pause / absent
              </span>
              <span className="wb-k">▶ = déplier le détail par activité</span>
            </div>
          </div>
        </div>

        {detail.length > 0 && (
          <details>
            <summary>Détail des tranches ({detail.length})</summary>
            <div className="wb-tablewrap">
              <table>
                <thead>
                  <tr>
                    <th>Jour</th>
                    <th>Début</th>
                    <th>Fin</th>
                    <th>Activité</th>
                    <th>Durée</th>
                  </tr>
                </thead>
                <tbody>{detail}</tbody>
              </table>
            </div>
          </details>
        )}

        <FriseTooltip tip={tip} />
      </div>
    </>
  );
}
